<template>
  <v-dialog v-model="showDialog" width="500" persistent>
    <v-card>
      <v-card-title class="flex items-center">
        <span>{{ t("categories.import") }}</span>
        <v-spacer />
        <v-btn
          icon="mdi-close"
          :aria-label="t('common.close')"
          variant="text"
          size="small"
          @click="closeDialog()"
        />
      </v-card-title>
      <v-card-text>
        <v-autocomplete
          v-model="selectedAccountId"
          :items="availableAccounts"
          :loading="loadingAccounts"
          item-title="name"
          item-value="id"
          :label="t('categories.selectSourceAccount')"
          :no-data-text="t('categories.noOtherAccounts')"
          clearable
        ></v-autocomplete>

        <div v-if="loadingCategories" class="flex justify-center py-4">
          <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions class="px-4 py-3">
        <v-btn @click="closeDialog()">{{ t("common.cancel") }}</v-btn>
        <v-spacer />
        <v-btn
          variant="flat"
          color="primary"
          :disabled="sourceCategories.length === 0 || loadingCategories || !selectedAccountId"
          @click="importCategories()"
        >
          {{ t("categories.copy") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { useCollection } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { db } from "@/firebase";
import { useUserStore } from "@/store";
import type { Account } from "@aircap/common/account";
import type {
  TrackedCategory,
  TrackedCategoryDirection,
  TrackedCategoryGroup,
} from "@aircap/common/trackedCategory";
import { collection, query, where } from "firebase/firestore";
import { computed, ref, watch } from "vue";

const { t } = useI18n();
const user = useUserStore();

const props = defineProps<{
  modelValue: boolean;
  accountId: string;
  direction: TrackedCategoryDirection;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  "import:categories": [categories: Array<{ name: string; amount: number; groupName?: string }>];
}>();

const showDialog = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const selectedAccountId = ref<string | null>(null);

const accountsSource = computed(() => {
  if (!user.supervisorOrgId) return null;
  return query(
    collection<Account>(db, "accounts"),
    where<Account>("supervisorOrgId", "==", user.supervisorOrgId),
  );
});

const { data: accountsData, pending: loadingAccounts } = useCollection<Account>(accountsSource);

const availableAccounts = computed(() => {
  if (!accountsData.value) return [];
  return accountsData.value
    .filter((p) => p.id !== props.accountId && !p.isArchived)
    .map((p) => ({ id: p.id, name: p.name }));
});

const sourceCategoriesSource = computed(() => {
  if (!selectedAccountId.value) return null;
  return collection<TrackedCategory>(db, `accounts/${selectedAccountId.value}/trackedCategories`);
});

const sourceGroupsSource = computed(() => {
  if (!selectedAccountId.value) return null;
  return collection<TrackedCategoryGroup>(
    db,
    `accounts/${selectedAccountId.value}/trackedCategoryGroups`,
  );
});

const { data: sourceCategoriesRaw, pending: loadingSourceCategories } =
  useCollection<TrackedCategory>(sourceCategoriesSource);
const { data: sourceGroupsRaw, pending: loadingGroups } =
  useCollection<TrackedCategoryGroup>(sourceGroupsSource);

const loadingCategories = computed(() => loadingSourceCategories.value || loadingGroups.value);

const groupNameById = computed(() => {
  const map = new Map<string, string>();
  for (const g of sourceGroupsRaw.value ?? []) {
    if (!g.deletedAt && g.direction === props.direction) map.set(g.id, g.name);
  }
  return map;
});

const sourceCategories = computed(() => {
  if (!sourceCategoriesRaw.value) return [];
  return sourceCategoriesRaw.value
    .filter((category) => !category.deletedAt && category.direction === props.direction)
    .map((category) => ({
      name: category.name,
      amount: category.amount,
      groupName: category.groupId ? groupNameById.value.get(category.groupId) : undefined,
    }));
});

watch(showDialog, (value) => {
  if (!value) {
    selectedAccountId.value = null;
  }
});

const closeDialog = () => {
  showDialog.value = false;
};

const importCategories = () => {
  if (loadingCategories.value || !selectedAccountId.value) return;
  if (sourceCategories.value.length === 0) return;

  emit(
    "import:categories",
    sourceCategories.value.map((c) => ({
      name: c.name,
      amount: c.amount,
      ...(c.groupName ? { groupName: c.groupName } : {}),
    })),
  );
  closeDialog();
};
</script>
